import React, { Component, createRef } from 'react';
import { Platform, View } from 'react-native';
import CountDown from 'react-native-countdown-component';

import { VFText } from '@vfgroup-oneplatform/foundation/Components';
import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { ToBiEmojis } from '@vfgroup-oneplatform/foundation/ToBi';
import Navigation from '@vfgroup-oneplatform/foundation/Utils/Navigation';
import { isFunction as _isFunction } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import Status from '../../Utils/AuthStatusEnum';
import LoginTestKeys from '../../Utils/LoginTestKeys';
import AuthContainer from '../AuthContainer/AuthContainer';

import styles from './VerifyCode.Styles';
import VerifyForm from './VerifyForm';

class VerifyCode extends Component {
  constructor(props) {
    super(props);
    const verifyCodeLength = Navigation.instance.getParam(this, 'verifyCodeLength', 4);

    this.getResendCodeTimeout = Navigation.instance.getParam(this, 'getResendCodeTimeout', () => 0);
    let resendCodeTimeout = _isFunction(this.getResendCodeTimeout) && this.getResendCodeTimeout();
    resendCodeTimeout = isNaN(resendCodeTimeout) ? 0 : resendCodeTimeout;

    this.state = {
      isVerifying: false,
      code: '',
      codeLength: verifyCodeLength,
      error: false,
      errorMessage: '',
      subErrorMessage: '',
      tobiEmoji: undefined,
      resetTimeOut: false,
      startAnimation: false,
      resendCodeDisabled: false,
      resendCodeRemainingTime: resendCodeTimeout,
      resendCodeTimeout,
      codeResentAt: null,
    };
  }

  componentDidMount = () => {
    this.handleAnimationAndTimer();
  };

  onFocus = () => {
    const { error } = this.state;
    if (error) {
      this.setState({ error: false });
    }
  };

  onBackPress = () => {
    Navigation.instance.goBack();
  };

  onEmojiEnd = ({ emoji }) => {
    if (emoji === this.state.tobiEmoji) {
      this.setState({ tobiEmoji: undefined, resetTimeOut: false });
    }
  };

  onEndAnimation = () => {
    this.setState({ startAnimation: false });
  };

  getErrorProps = (showErrorBox, errorMessage, subErrorMessage) => {
    const errorConfig = Navigation.instance.getParam(this, 'errorConfig', {});
    return {
      showErrorBox,
      errorMessage,
      subErrorMessage,
      style: styles.error,
      ...errorConfig,
    };
  };

  getErrorPropsMemoized = memoize(this.getErrorProps);

  secondLabelOnPress = () => {
    const onSecondLabelPress = Navigation.instance.getParam(this, 'onSecondLabelPress', () => null);
    onSecondLabelPress();
  };

  handleOnVerify = async code => {
    const onCodeSubmit = Navigation.instance.getParam(this, 'onCodeSubmit', () => null);
    const onVerifyCodeSuccess = Navigation.instance.getParam(
      this,
      'onVerifyCodeSuccess',
      () => null,
    );
    const phone = Navigation.instance.getParam(this, 'phone', null);

    try {
      const result = await onCodeSubmit(phone, code);
      if (result.status === Status.Success) {
        onVerifyCodeSuccess(result.response, result);
      } else {
        this.handleOnVerifyCodeFail(result, ToBiEmojis.Wink);
      }
    } catch (error) {
      this.handleOnVerifyCodeFail(error, ToBiEmojis.Sad);
    }
  };

  handleOnVerifyCodeFail(result, tobiEmoji) {
    const { resendCodeDisabled, codeResentAt, resendCodeTimeout } = this.state;

    const isClearValidFunc =
      this.codeVerify && this.codeVerify.current && _isFunction(this.codeVerify.current.clear);

    this.setState(
      {
        isVerifying: false,
        error: true,
        tobiEmoji,
        errorMessage: result.errorMessage,
        subErrorMessage: result.subErrorMessage,
        showCountDown: true,
        resendCodeRemainingTime:
          resendCodeDisabled && codeResentAt
            ? resendCodeTimeout - Date.now() + codeResentAt
            : resendCodeTimeout,
      },
      () => {
        isClearValidFunc && this.codeVerify.current.clear();
      },
    );
  }

  handlerOnFulfill = code => {
    this.setState(
      {
        isVerifying: true,
        tobiEmoji: ToBiEmojis.Whistle,
        code,
      },
      () => this.handleOnVerify(code),
    );
  };

  handleAnimationAndTimer = () => {
    const { resendCodeRemainingTime, startAnimation, resendCodeTimeout } = this.state;

    if (resendCodeRemainingTime > 0) {
      this.setState({
        resendCodeDisabled: true,
        codeResentAt: Date.now(),
      });
      setTimeout(() => {
        this.setState({
          resendCodeDisabled: false,
          resendCodeRemainingTime: resendCodeTimeout,
          codeResentAt: null,
        });
      }, resendCodeTimeout);
    }

    if (startAnimation) {
      this.setState({ resetTimeOut: true });
    }

    this.setState({
      tobiEmoji: ToBiEmojis.Smug,
      startAnimation: true,
      error: false,
    });
  };

  resendSms = () => {
    const getSMSCode = Navigation.instance.getParam(this, 'getSMSCode', () => null);
    const phone = Navigation.instance.getParam(this, 'phone', null);

    this.handleAnimationAndTimer();

    getSMSCode(phone).catch(error => {
      this.setState({
        error: true,
        errorMessage: error.errorMessage,
        subErrorMessage: error.subErrorMessage,
        resendCodeDisabled: false,
      });
    });
  };

  codeVerify = createRef();

  renderCountDown = (
    withCountdown,
    resendCodeDisabled,
    countdownProps,
    resendCodeTimeout,
    theme,
  ) => {
    if (!withCountdown || !resendCodeDisabled) {
      return null;
    }

    const { separatorStyle, digitStyle, digitTxtStyle, containerStyle, bracketStyle, onFinish } =
      countdownProps || {};
    return (
      <View style={[styles.countDownContainer, containerStyle]}>
        <VFText i18nKey={'('} style={[styles.bracket(theme), bracketStyle]} />
        <CountDown
          until={resendCodeTimeout / 1000}
          timeToShow={['M', 'S']}
          timeLabels={{ m: '', s: '' }}
          showSeparator
          separatorStyle={[styles.separatorStyle(theme), separatorStyle]}
          digitStyle={[styles.digitStyle, digitStyle]}
          digitTxtStyle={[styles.digitTxtStyle(theme), digitTxtStyle]}
          onFinish={_isFunction(onFinish) && onFinish}
        />
        <VFText i18nKey={')'} style={[styles.bracket(theme), bracketStyle]} />
      </View>
    );
  };

  render() {
    const {
      error,
      errorMessage,
      subErrorMessage,
      tobiEmoji,
      startAnimation,
      resetTimeOut,
      isVerifying,
      codeLength,
      resendCodeDisabled,
      resendCodeRemainingTime,
    } = this.state;
    const { theme, navigation } = this.props;
    const withCountdown = Navigation.instance.getParam(this, 'withCountdown', false);
    const countdownProps = Navigation.instance.getParam(this, 'countdownProps', {});
    const phone = Navigation.instance.getParam(this, 'phone', null);
    const withLabels = Navigation.instance.getParam(this, 'withLabels', () => null);
    const renderTitle = !isVerifying
      ? Navigation.instance.getParam(this, 'renderTitle', () => { })
      : () => { };
    const keyboardType = Navigation.instance.getParam(this, 'keyboardType', 'number-pad');
    const codeInputTextStyle = Navigation.instance.getParam(this, 'codeInputTextStyle', null);
    return (
      <AuthContainer
        tobiSize={114}
        tobiEmoji={tobiEmoji}
        onEmojiEnd={this.onEmojiEnd}
        startAnimation={startAnimation}
        resetTimeOut={resetTimeOut}
        onEndAnimation={this.onEndAnimation}
        errorProps={this.getErrorPropsMemoized(error, errorMessage, subErrorMessage)}
        renderTitle={renderTitle}
        bubbleText="verification_description_receive_sms"
        tobiIconAccessibilityLabel={LoginTestKeys.loginTobiIcon}
        renderTobiLessIconView={Navigation.instance.getParam(
          this,
          'renderVerifyLoginIconView',
          null,
        )}
        firstLabelTitle={withLabels ? 'soft_login_footer_first_label' : null}
        firstLabelDisabled
        secondLabelTitle={withLabels ? 'soft_login_footer_second_label' : null}
        secondLabelOnPress={this.secondLabelOnPress}
        firstLabelTestID={LoginTestKeys.loginRegisteredTxt}
        secondLabelTestId={LoginTestKeys.loginUsernameLink}
        isLoading={isVerifying}
        loadingText="verification_loading_login_text"
        {...Platform.select({
          web: {
            backBtn: true,
            backBtnPress: () => navigation?.pop(1)
          },
          default: {

          }
        })
        }
      >
        <VerifyForm
          isVerifying={isVerifying}
          error={error}
          codeLength={codeLength}
          codeInputTextStyle={codeInputTextStyle}
          resendCodeDisabled={resendCodeDisabled}
          withCountdown={withCountdown}
          countdownProps={countdownProps}
          resendCodeTimeout={resendCodeRemainingTime}
          phone={phone}
          renderCountDown={this.renderCountDown}
          onBackPress={this.onBackPress}
          codeVerify={this.codeVerify}
          resendSms={this.resendSms}
          theme={theme}
          handlerOnFulfill={this.handlerOnFulfill}
          onFocus={this.onFocus}
          keyboardType={keyboardType}
        />
      </AuthContainer>
    );
  }
}

VerifyCode.defaultProps = {
  theme: LightThemeColors,
};

VerifyCode.propTypes = {
  theme: PropTypes.object,
  navigation: PropTypes.object.isRequired,
};

export default withTheme(VerifyCode);
